// eslint-disable-next-line no-useless-escape
const EMAIL_REGEX =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const PASSWORD_MIN_SIZE = 8;
const PASSWORD_AT_LEST_SECTIONS = 3;
const PASSWORD_LOWER_CASE_REGEX = /[a-z]/;
const PASSWORD_UPPER_CASE_REGEX = /[A-Z]/;
const PASSWORD_NUMBERS_REGEX = /\d/;
const DECIMAL_NUMBERS_REGEX = /^\d+(\.\d{1,2})?$/;
const URL_REGEX = /^(?:http(s)?:\/\/)?[\w.-]+(?:\.[\w.-]+)+[\w\-._~:/?#[\]@!$&'()*+,;=.]+$/;
const PASSWORD_SPECIAL_CHARACTERS_REGEX = /[@]/;
const ALPHANUMERIC_REGEX = /[^a-zA-Z0-9]/g;

export function required(errorMessage = 'This field is required.') {
  return (value) =>
    // Any value that evalutes to false is considered an error (undefined, null, '', false)
    // with the expection of the number zero (0)
    !value && typeof value !== 'number' ? errorMessage : null;
}

export function requiredNumber(errorMessage = 'This field is required.') {
  return (value) => (Number.isNaN(value) || !value ? errorMessage : null);
}

export function requiredIfTrue(checkCallback, errorMessage = 'This field is required.') {
  return (value) =>
    // Any value that evalutes to false is considered an error (undefined, null, '', false)
    // with the expection of the number zero (0)
    !value && typeof value !== 'number' ? (checkCallback(value) ? errorMessage : null) : null;
}

export function maxLength(length = 60, errorMessage) {
  const message = errorMessage || `Must be under ${length} characters.`;

  return (value) => (value && value.length > length ? message : null);
}

export function words(wordsCount = 2, errorMessage) {
  const message = errorMessage || `Must be over ${wordsCount} word.`;

  return (value) => {
    if (!value) {
      return null;
    }

    if (value.split(' ').filter(Boolean).length >= wordsCount) {
      return null;
    }

    return message;
  };
}

export function minLength(length = 1, errorMessage) {
  const message = errorMessage || `Must be over ${length} characters.`;

  return (value) => (value && value.length < length ? message : null);
}

export function oneOf(listToCheck = [], errorMessage = 'This field is required.') {
  return (value) => (listToCheck.includes(value) ? null : errorMessage);
}

export function email(errorMessage = 'Please enter a valid email.') {
  return (value) => (!value || value.match(EMAIL_REGEX) ? null : errorMessage);
}

export function password(
  errorMessage = 'Your password must contain minimum eight characters, at least one uppercase letter, one lowercase letter and one number.',
) {
  return (value) => {
    // If no value or the min length is not reached throw error
    if (!value || value.length < PASSWORD_MIN_SIZE) {
      return errorMessage;
    }

    const validSections = [
      PASSWORD_LOWER_CASE_REGEX,
      PASSWORD_UPPER_CASE_REGEX,
      PASSWORD_NUMBERS_REGEX,
      PASSWORD_SPECIAL_CHARACTERS_REGEX,
    ].filter((regex) => regex.test(value)).length;

    return validSections >= PASSWORD_AT_LEST_SECTIONS ? null : errorMessage;
  };
}

export function between(
  { allowEmpty, min, max } = {},
  overErrorMessage = `Your value is over the maximum value of ${max}`,
  underErrorMessage = `Your value is under the minimum value of ${min}`,
) {
  return (value) => {
    if ((!!allowEmpty && value === 0) || Number.isNaN(Number.parseInt(value, 10))) {
      return null;
    }

    return typeof min === 'number' && value < min
      ? underErrorMessage
      : typeof max === 'number' && value > max
      ? overErrorMessage
      : null;
  };
}

export function isDecimalNumber(errorMessage = 'Your value is not a valid number.') {
  return (value) => (!value || DECIMAL_NUMBERS_REGEX.test(value) ? null : errorMessage);
}

export function isUrl(value) {
  return URL_REGEX.test(value);
}

export function isValidUrl(errorMessage = 'Your link is not valid.') {
  return (value) => (!value || isUrl(value) ? null : errorMessage);
}

export function asyncUniq(validator, errorMessage = 'This field must be uniq') {
  return (value) =>
    validator(value)
      .then((exists) => {
        if (exists) {
          throw errorMessage;
        }

        return null;
      })
      .catch(() => {
        throw errorMessage;
      });
}

export function buildValidator(fieldName, validator) {
  return (value) => {
    if (!validator) {
      return;
    }

    const errorMessage = Array.isArray(validator)
      ? validator.map((val) => val(value)).find((result) => !!result) || null
      : validator(value);

    return errorMessage ? { [fieldName]: errorMessage } : null;
  };
}

export function combineBuiltValidators(validators) {
  return (value) => {
    const validationResult = validators
      .filter((validator) => typeof validator === 'function')
      .map((validator) => validator(value))
      .filter(Boolean);

    if (!validationResult.length) {
      return;
    }

    return validationResult.reduce((acc, current) => Object.assign(acc, current), {});
  };
}

export function buildAsyncValidator(fieldName, validator) {
  return (value) => {
    if (!validator || !value) {
      return;
    }

    return validator(value)
      .then(() => null)
      .catch((errorMessage) => ({ [fieldName]: errorMessage }));
  };
}

export function isDuplicated(values, errorMessage = 'This value already exists.') {
  return (value) => {
    if (values.indexOf(value) !== values.lastIndexOf(value)) {
      return errorMessage;
    }
  };
}

export const buildDynamicRegexWithSpecialCharacters = (value) =>
  new RegExp(value.replace(ALPHANUMERIC_REGEX, '\\$&'), 'i');
